/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from "react";
import { Box, Button, Grid, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useFormik } from "formik";
import { RootState, useAppDispatch } from "@store/store";
import CustomUploadFileV2 from "@components/CustomUploadFileV2";
import { useLazyGetBanksByCodeQuery } from "@api/catalogs";
import {
  useLazyGetBankAccountsQuery,
  usePostBankAccountsMutation,
  usePostBankAccountStatementMutation,
  usePutBankAccountMutation,
  useDeleteBankAccountStatementMutation,
} from "@api/applications";
import { useLazyGetDocumentAppIdQuery } from "@api/documents";
import { InactivityHandler } from "@components/InactivityHandler";
import { UUID } from "crypto";
import { DocumentResponse } from "@interfaces/response";
import { PayloadBankAccount } from "@interfaces/request";
import { ApiError } from "@interfaces/response";
import { usePatchPersonMutation } from "@api/personasApi";
import { DOCUMENTS_CATALOG } from "@helpers/constantes";
import CustomRightImage from "@components/CustomRightImagev2";
import { setModalErrorVisible } from "@store/slices/appSlice";
import * as Sentry from "@sentry/react";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import { createEmptyBankAccount, getFormikArrayError, identifyBankAccounts } from "@helpers/funciones";
import useGenerateAnexo from "@hooks/useGenerateAnexo";
import { BankAccountFormItem } from "@interfaces/catalogs";

const DEFAULT_NUMBER_BANK_STATEMENTS = 1;

const InformacionBancaria = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const interfaceConfig = useInterfaceConfig();

  const application = useSelector((state: RootState) => state.register.application);
  const personalData = useSelector((state: RootState) => state.register.personal_data);
  const personAval = useSelector((state: RootState) => state.bofu.aval);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingBankName, setLoadingBankName] = useState<boolean>(false);
  const [loadingBankStatement, setLoadingBankStatement] = useState<boolean>(false);

  const [triggerGetBanks] = useLazyGetBanksByCodeQuery();
  const [getBankAccounts] = useLazyGetBankAccountsQuery();
  const [getDocumentsByAppId] = useLazyGetDocumentAppIdQuery();
  const [triggerPutBankAccount] = usePutBankAccountMutation();
  const [triggerPostBankAccount] = usePostBankAccountsMutation();
  const [patchPersonData] = usePatchPersonMutation();
  const [triggerPostBankAccountStatement] = usePostBankAccountStatementMutation();
  const [triggerDeleteBankAccountStatement] = useDeleteBankAccountStatementMutation();
  const generateAnexo = useGenerateAnexo();

  const getNumberStatements = (): number => {
    return (
      interfaceConfig.formularios?.informacion_bancaria_aval?.numero_estados_cuenta || DEFAULT_NUMBER_BANK_STATEMENTS
    );
  };

  const formik = useFormik({
    initialValues: {
      accounts: [createEmptyBankAccount({ number_statements: getNumberStatements() })],
    },
    validationSchema: yup.object().shape({
      accounts: yup.array().of(
        yup.object().shape({
          clabe: yup
            .string()
            .min(18, "Formato incorrecto, debe tener 18 dígitos")
            .required("Campo requerido.")
            .test("unique", "La cuenta bancaria ya se encuentra registrada.", function (value) {
              const num_results: number = formik.values.accounts.filter(
                (item: BankAccountFormItem) => item.clabe === value,
              ).length;
              return num_results > 1 ? false : true;
            }),
          bank_id: yup.string().required("Clabe bancaria incorrecta, validar nuevamente."),
          statements: yup.array().of(
            yup.object().shape({
              bank_statement_document_id: yup.string().required("Campo requerido."),
              document_id: yup.string().required("Campo requerido."),
              document_order: yup.number().required("Campo requerido."),
              document_value: yup.object(),
            }),
          ),
        }),
      ),
    }),
    onSubmit: async (_values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        const response_patch = await patchPersonData({
          personId: personAval?.id as UUID,
          body: {
            bank_references_completed: true,
            bank_references_status: "COMPLETO",
          },
        }).unwrap();

        await generateAnexo(application.id, response_patch.data, "anexo_1");

        navigate("/producto/creditosimple/docs_aval");
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "InformacionBancariaAval-submit",
          },
        });
        const api_error = error as ApiError;
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  const getBankNameByCode = useCallback(async (bank_code: string) => {
    try {
      const bank_info = await triggerGetBanks(bank_code).unwrap();
      if (bank_info) {
        return {
          id: bank_info.id,
          name: bank_info.name,
        };
      } else {
        return null;
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "InformacionBancariaAval-getBankNameByCode",
        },
      });
      return null;
    }
  }, []);

  const handleChangeCLABE = async (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { name, value } = event.target;
    const account = formik.values.accounts[index];
    const only_numbers = value.replace(/[^0-9]/g, "");
    formik.setFieldValue(name, only_numbers);

    if (only_numbers.length === 18) {
      setLoadingBankName(true);
      const bank_code = only_numbers.substring(0, 3);
      const bank_info = await getBankNameByCode(bank_code);

      if (bank_info) {
        // guardar la cuenta bancaria en la bd
        const payload: PayloadBankAccount = {
          loan_application_id: application.id,
          client_details_id: null,
          person_id: personAval?.id,
          account: 0,
          account_holder: "",
          clabe: only_numbers,
          bank_id: bank_info.id,
          documents: [],
        };

        if (account.id) {
          await triggerPutBankAccount({
            solicitudId: application.id,
            bankAccountId: account.id,
            body: payload,
          }).unwrap();
        } else {
          const response = await triggerPostBankAccount({
            solicitudId: application.id,
            body: payload,
          }).unwrap();

          formik.setFieldValue(`accounts[${index}].id`, response.id);
        }

        // setear la informacion del banco en el formulario
        formik.setFieldValue(`accounts[${index}].bank_id`, bank_info.id);
        formik.setFieldValue(`accounts[${index}].bank_name`, bank_info.name || "");
      } else {
        formik.setFieldValue(`accounts[${index}].bank_id`, "");
        formik.setFieldValue(`accounts[${index}].bank_name`, "");
      }

      setTimeout(() => {
        formik.validateForm();
        formik.setFieldTouched(`accounts[${index}].bank_id`, true);
        formik.setFieldTouched(`accounts[${index}].bank_name`, true);
      }, 500);
    }
  };

  const handleUploadBankStatement = async (
    newUpload: DocumentResponse | undefined,
    accountIndex: number,
    statementIndex: number,
  ) => {
    try {
      setLoadingBankStatement(true);

      const copy_accounts: BankAccountFormItem[] = [...formik.values.accounts];
      const account: BankAccountFormItem = copy_accounts[accountIndex];
      const statement = account.statements[statementIndex];

      if (newUpload) {
        if (account.id) {
          const response = await triggerPostBankAccountStatement({
            solicitudId: application.id,
            accountId: account.id,
            body: {
              loan_application_bank_account_id: account.id,
              document_id: newUpload.id,
              document_order: statementIndex + 1,
            },
          }).unwrap();

          copy_accounts[accountIndex].statements[statementIndex] = {
            bank_statement_document_id: response.id,
            document_id: newUpload.id,
            document_order: statementIndex + 1,
            document_value: newUpload,
          };

          // actualizar el formulario
          formik.setFieldValue("accounts", copy_accounts);
        }
      } else {
        if (account.id && statement.bank_statement_document_id) {
          await triggerDeleteBankAccountStatement({
            solicitudId: application.id,
            accountId: account.id,
            documentId: statement.bank_statement_document_id,
          }).unwrap();

          copy_accounts[accountIndex].statements[statementIndex] = {
            bank_statement_document_id: undefined,
            document_id: undefined,
            document_order: statementIndex + 1,
            document_value: undefined,
          };

          // actualizar el formulario
          formik.setFieldValue("accounts", copy_accounts);
        }
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "InformacionBancariaAval-handleUploadBankStatement",
        },
      });
    } finally {
      setLoadingBankStatement(false);
    }
  };

  const getInitialData = async () => {
    try {
      setIsLoading(true);
      if (!personAval) {
        navigate("/producto/creditosimple/docs_aval");
        return;
      }

      const [list_bank_accounts, documents] = await Promise.all([
        getBankAccounts(application.id).unwrap(),
        getDocumentsByAppId({
          loanApplicationId: application.id,
          personId: personAval?.id,
          status: true,
        }).unwrap(),
      ]);

      // filtrar las cuentas bancarias de la empresa
      const { aval_accounts } = identifyBankAccounts({
        bank_accounts: list_bank_accounts,
        aval: personAval,
      });

      const accounts: BankAccountFormItem[] = [];
      const num_statements_required = getNumberStatements();

      for (let index = 0; index < aval_accounts.length; index++) {
        const account = aval_accounts[index];

        // consultar el nombre del banco
        const bank_code = (account.clabe || "").toString().substring(0, 3);
        const bank_info = await getBankNameByCode(bank_code);

        const new_account: BankAccountFormItem = {
          id: account.id,
          clabe: account.clabe || "",
          bank_id: bank_info?.id || "",
          bank_name: bank_info?.name || "",
          statements: Array(num_statements_required).fill(undefined),
        };

        for (let index = 0; index < num_statements_required; index++) {
          const account_document = account.documents[index];
          if (account_document) {
            new_account.statements[index] = {
              bank_statement_document_id: account_document.bank_statement_document_id,
              document_id: account_document.document_id,
              document_order: account_document.document_order || 0,
              document_value: documents.find((item) => item.id === account_document.document_id),
            };
          }
        }

        console.log("new_account", new_account);

        accounts.push(new_account);
      }

      if (accounts.length) {
        formik.setFieldValue("accounts", accounts);
      }
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "InformacionBancariaAval-initial_fetch",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getInitialData();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box maxWidth="sm" mx="auto" mt={4} px={2}>
          <Typography
            textAlign={"center"}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Información bancaria
          </Typography>

          <Typography
            textAlign={"center"}
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.dark}
            mt={2}
          >
            <strong>1</strong> de 1
          </Typography>

          <Typography
            textAlign={"center"}
            color={theme?.palette?.primary?.main}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={600}
            mt={2}
          >
            Ingresa los datos de tu cuenta principal, dónde concentras la mayoría de tu actividad bancaría.
          </Typography>

          <form onSubmit={formik.handleSubmit}>
            {formik.values.accounts.map((account: BankAccountFormItem, index: number) => (
              <Box key={index} mt={index > 0 ? 4 : 0}>
                <TextField
                  name={`accounts[${index}].clabe`}
                  label="Cuenta CLABE"
                  margin="normal"
                  type="text"
                  fullWidth
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChangeCLABE(event, index)}
                  value={formik.values.accounts[index].clabe}
                  error={
                    Boolean(getFormikArrayError(formik, "accounts", index, "clabe")) ||
                    Boolean(getFormikArrayError(formik, "accounts", index, "bank_id"))
                  }
                  helperText={
                    getFormikArrayError(formik, "accounts", index, "clabe") ||
                    getFormikArrayError(formik, "accounts", index, "bank_id")
                  }
                  inputProps={{ maxLength: 18 }}
                  disabled={isLoading || formik.isSubmitting || loadingBankName}
                />

                <TextField
                  name={`accounts[${index}].bank_name`}
                  label="Banco"
                  margin="normal"
                  fullWidth
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.accounts[index].bank_name}
                  disabled={true}
                />

                <Box my={2}>
                  <Typography
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    fontWeight={theme?.palette?.fontWeights?.regular}
                    color={theme?.palette?.primary?.dark}
                  >
                    Debes cargar los estado de cuenta bancario de los últimos{" "}
                    {interfaceConfig.formularios?.informacion_bancaria_aval?.numero_estados_cuenta ||
                      DEFAULT_NUMBER_BANK_STATEMENTS}{" "}
                    meses de una o mas cuentas para cubrir la capacidad de pago necesaria para el crédito solicitado.
                  </Typography>
                </Box>

                {account.statements.map((statement, statementIndex) => (
                  <Box key={`bank_statement_${index}_${statementIndex}`}>
                    <CustomUploadFileV2
                      mt={2}
                      inputName={`bank_statement_${index}_${statementIndex}`}
                      uploadedFile={statement?.document_value}
                      setUploadedFile={(newUpload) => {
                        handleUploadBankStatement(newUpload, index, statementIndex);
                      }}
                      optionalName={`Estado de cuenta ${statementIndex + 1}`}
                      optionalDescription=""
                      metadata={{
                        fileCode: DOCUMENTS_CATALOG.estado_cuenta_bancario.code,
                        uploadType: "person",
                        personId: personAval?.id as UUID,
                        clientDetailsId: personalData.id,
                        personType: "accionista_mayoritario",
                      }}
                      disabled={isLoading || formik.isSubmitting || loadingBankStatement || !account.id}
                      required
                    />
                  </Box>
                ))}
              </Box>
            ))}

            <Grid container columnSpacing={1} my={4}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="blue_outlined"
                  sx={{ padding: "0px", height: "100%" }}
                  onClick={() => navigate("/producto/creditosimple/docs_aval")}
                  disabled={isLoading || formik.isSubmitting || loadingBankStatement}
                >
                  Regresar al inicio
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  type="submit"
                  variant="default"
                  disabled={isLoading || !formik.isValid || formik.isSubmitting}
                >
                  Continuar
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default InformacionBancaria;
